import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  TrendingUp, Landmark, Wallet, RefreshCw, AlertTriangle, HelpCircle,
  ChevronRight, Receipt, Users, HandCoins, Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  Training: { bar: 'bg-emerald-500', text: 'text-emerald-700', soft: 'bg-emerald-50', ring: 'border-emerald-200' },
  Venue: { bar: 'bg-amber-500', text: 'text-amber-700', soft: 'bg-amber-50', ring: 'border-amber-200' },
  Consultancy: { bar: 'bg-sky-500', text: 'text-sky-700', soft: 'bg-sky-50', ring: 'border-sky-200' },
  Unclassified: { bar: 'bg-slate-400', text: 'text-slate-600', soft: 'bg-slate-50', ring: 'border-slate-200' },
};

const FunderCorporatePnL = ({ globalFY, getAuthHeaders, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusScope, setStatusScope] = useState('commitment');
  const [drill, setDrill] = useState(null); // { title, kind, rows }
  const [repayments, setRepayments] = useState([]);
  const [showRepayments, setShowRepayments] = useState(false);
  const [repayForm, setRepayForm] = useState(null); // { amount, repayment_date, category, note }
  const [saving, setSaving] = useState(false);

  const fetchRepayments = useCallback(async () => {
    if (!globalFY) return;
    try {
      const res = await axios.get(`${API}/pnl/funder-repayments?financial_year=${globalFY}`, { headers: getAuthHeaders() });
      setRepayments(res.data.repayments || []);
    } catch (e) { /* ignore */ }
  }, [globalFY, getAuthHeaders]);

  const fetchData = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/pnl/statement?financial_year=${globalFY}&status_scope=${statusScope}`,
        { headers: getAuthHeaders() }
      );
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load P&L statement');
    } finally {
      setLoading(false);
    }
  }, [globalFY, statusScope, getAuthHeaders]);

  useEffect(() => { fetchData(); fetchRepayments(); }, [fetchData, fetchRepayments]);

  const saveRepayment = async () => {
    const amt = parseFloat(repayForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      setSaving(true);
      await axios.post(`${API}/pnl/funder-repayments`, {
        financial_year: globalFY, amount: amt, repayment_date: repayForm.repayment_date,
        category: repayForm.category, note: repayForm.note,
      }, { headers: getAuthHeaders() });
      toast.success('Repayment recorded');
      setRepayForm(null);
      fetchData(); fetchRepayments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save repayment');
    } finally { setSaving(false); }
  };

  const deleteRepayment = async (id) => {
    try {
      await axios.delete(`${API}/pnl/funder-repayments/${id}`, { headers: getAuthHeaders() });
      toast.success('Repayment deleted');
      fetchData(); fetchRepayments();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const fmt = (v) => formatCurrency(v);
  const pct = (r) => `${(r * 100).toFixed(0)}%`;

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="fcpnl-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  if (!data) return null;

  const ms = data.money_story;
  const cp = data.corporate_pnl;

  const openTypeDrill = (split) => {
    if (!split.estimates?.length) return;
    setDrill({
      title: `${split.type} — ${split.estimates.length} valid estimate(s)`,
      kind: 'estimates',
      rows: split.estimates,
    });
  };
  const openExpenseDrill = (title, rows) => {
    if (!rows?.length) { toast.info('No line items for this bucket'); return; }
    setDrill({ title, kind: 'expenses', rows });
  };

  return (
    <div className="space-y-8" data-testid="funder-corporate-pnl">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Funder &amp; Corporate P&amp;L</h2>
          <p className="text-sm text-slate-500">A strict financial statement for FY {globalFY}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusScope} onValueChange={setStatusScope}>
            <SelectTrigger className="w-[260px]" data-testid="fcpnl-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="commitment">All expenses except Rejected (commitment)</SelectItem>
              <SelectItem value="realized">Only Paid + Partially Paid (realized)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="fcpnl-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ===== Money Story ===== */}
      <section data-testid="fcpnl-money-story">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-2xl p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-lg">
            <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
              <TrendingUp className="w-4 h-4" /> Valid Revenue
            </div>
            <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums" data-testid="ms-valid-revenue">{fmt(ms.valid_revenue)}</p>
            <p className="mt-1 text-xs text-slate-400">From {ms.project_count} valid estimates (excl. GST)</p>
          </div>
          <div className="rounded-2xl p-6 bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg">
            <div className="flex items-center gap-2 text-indigo-100 text-sm font-medium">
              <Landmark className="w-4 h-4" /> Funder Earnings
            </div>
            <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums" data-testid="ms-funder-earnings">{fmt(ms.funder_earnings)}</p>
            <p className="mt-1 text-xs text-indigo-100/80">Funder share across all project types</p>
          </div>
          <div className="rounded-2xl p-6 bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg">
            <div className="flex items-center gap-2 text-emerald-100 text-sm font-medium">
              <Wallet className="w-4 h-4" /> Corporate Team Net P&amp;L
            </div>
            <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums" data-testid="ms-corp-net">{fmt(ms.corporate_net_pnl)}</p>
            <p className="mt-1 text-xs text-emerald-100/80">After funder share &amp; all expenses</p>
          </div>
        </div>
        {(ms.unclassified_count > 0 || ms.discrepancy_count > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {ms.unclassified_count > 0 && (
              <Badge variant="outline" className="border-slate-300 text-slate-600 gap-1" data-testid="ms-unclassified">
                <HelpCircle className="w-3 h-3" /> {ms.unclassified_count} unclassified (0% funder share)
              </Badge>
            )}
            {ms.discrepancy_count > 0 && (
              <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 gap-1" data-testid="ms-discrepancy">
                <AlertTriangle className="w-3 h-3" /> {ms.discrepancy_count} type discrepancy to review
              </Badge>
            )}
          </div>
        )}
      </section>

      {/* ===== Business Split ===== */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-3">Business Split by Project Type</h3>
        <Card className="border-slate-200 overflow-hidden">
          <Table data-testid="fcpnl-split-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Project Type</TableHead>
                <TableHead className="text-center">Funder %</TableHead>
                <TableHead className="text-center">Projects</TableHead>
                <TableHead className="text-right">Valid Revenue</TableHead>
                <TableHead className="text-right">Funder Share</TableHead>
                <TableHead className="text-right">Corporate Retained</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.business_split.map((s) => {
                const st = TYPE_STYLES[s.type] || TYPE_STYLES.Unclassified;
                return (
                  <TableRow
                    key={s.type}
                    className={`cursor-pointer hover:bg-slate-50 ${s.count === 0 ? 'opacity-50' : ''}`}
                    onClick={() => openTypeDrill(s)}
                    data-testid={`split-row-${s.type}`}
                  >
                    <TableCell>
                      <span className="inline-flex items-center gap-2 font-medium">
                        <span className={`w-2.5 h-2.5 rounded-full ${st.bar}`}></span>
                        {s.type}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">{pct(s.funder_rate)}</TableCell>
                    <TableCell className="text-center tabular-nums">{s.count}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(s.revenue_excl_gst)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${st.text}`}>{fmt(s.funder_share)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmt(s.corp_retained)}</TableCell>
                    <TableCell className="text-right">
                      {s.count > 0 && <ChevronRight className="w-4 h-4 text-slate-400 inline" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <tfoot>
              <TableRow className="bg-slate-900 text-white hover:bg-slate-900">
                <TableCell className="font-semibold text-white">Total</TableCell>
                <TableCell></TableCell>
                <TableCell className="text-center tabular-nums text-white">{ms.project_count}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold text-white">{fmt(ms.valid_revenue)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold text-indigo-300">{fmt(ms.funder_earnings)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold text-emerald-300">{fmt(cp.revenue_after_funder)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </tfoot>
          </Table>
        </Card>
      </section>

      {/* ===== Funder Earnings + Corporate Team P&L ===== */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funder Earnings */}
        <Card className="border-indigo-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900">Funder Earnings</h3>
            </div>
            <div className="space-y-2" data-testid="fcpnl-funder-earnings">
              {data.funder_earnings.by_type.filter(b => b.funder_share > 0 || b.revenue_excl_gst > 0).map((b) => {
                const st = TYPE_STYLES[b.type] || TYPE_STYLES.Unclassified;
                return (
                  <div key={b.type} className={`flex items-center justify-between rounded-lg px-4 py-3 ${st.soft}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${st.bar}`}></span>
                      <span className="text-sm font-medium text-slate-700">{b.type}</span>
                      <span className="text-xs text-slate-400">{pct(b.funder_rate)} of {fmt(b.revenue_excl_gst)}</span>
                    </div>
                    <span className={`font-semibold tabular-nums ${st.text}`}>{fmt(b.funder_share)}</span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between border-t-2 border-indigo-200 pt-3 mt-2">
                <span className="font-semibold text-slate-900">Total Funder Earnings</span>
                <span className="text-xl font-bold text-indigo-700 tabular-nums">{fmt(data.funder_earnings.total)}</span>
              </div>
            </div>

            {/* Owed to Funder — salary advanced as a loan + funder share */}
            {data.funder_settlement && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="fcpnl-funder-settlement">
                <div className="flex items-center gap-2 mb-2">
                  <HandCoins className="w-4 h-4 text-amber-600" />
                  <h4 className="text-sm font-semibold text-amber-900">Owed to Funder</h4>
                </div>
                <p className="text-xs text-amber-700/80 mb-3">The Funder advances the team's salary as a loan, repayable by the Corporate Team — shown here with the Funder's revenue share.</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Funder Share (revenue cut)</span>
                    <span className="tabular-nums">{fmt(data.funder_settlement.funder_share)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Salary Advance (loan to repay)</span>
                    <span className="tabular-nums">{fmt(data.funder_settlement.salary_advance)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-amber-200 pt-2 mt-1">
                    <span className="font-medium text-slate-700">Total Owed to Funder</span>
                    <span className="tabular-nums font-medium">{fmt(data.funder_settlement.total_owed_to_funder)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Less: Repaid</span>
                    <span className="tabular-nums text-emerald-700">({fmt(data.funder_settlement.repaid)})</span>
                  </div>
                  <div className="flex items-center justify-between border-t-2 border-amber-300 pt-2 mt-1">
                    <span className="font-semibold text-amber-900">Outstanding to Funder</span>
                    <span className="text-lg font-bold text-amber-700 tabular-nums" data-testid="fcpnl-total-owed-funder">{fmt(data.funder_settlement.outstanding)}</span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <button onClick={() => setShowRepayments(true)} className="text-xs font-medium text-amber-700 hover:underline" data-testid="fcpnl-manage-repayments">
                    Manage repayments ({repayments.length})
                  </button>
                  <Button size="sm" variant="outline" className="h-7 border-amber-300 text-amber-800" onClick={() => setRepayForm({ amount: '', repayment_date: new Date().toISOString().slice(0, 10), category: 'funder_share', note: '' })} data-testid="fcpnl-add-repayment">
                    <Plus className="w-3.5 h-3.5 mr-1" />Record repayment
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Corporate Team P&L — waterfall statement */}
        <Card className="border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-semibold text-slate-900">Corporate Team P&amp;L</h3>
            </div>
            <div className="space-y-1 text-sm" data-testid="fcpnl-corp-pnl">
              <StmtRow label="Valid Revenue" value={fmt(cp.valid_revenue)} strong />
              <StmtRow label="Less: Funder Share" value={`(${fmt(cp.funder_share)})`} negative />
              <StmtRow label="Revenue after Funder Share" value={fmt(cp.revenue_after_funder)} divider strong />
              <StmtRow
                label="Less: Project Expenses"
                value={`(${fmt(cp.project_expenses)})`}
                negative clickable
                onClick={() => openExpenseDrill('Project Expenses', cp.project_expenses_list)}
                icon={<Receipt className="w-3.5 h-3.5" />}
              />
              <StmtRow
                label="Less: HR Salary (advanced by Funder)"
                value={`(${fmt(cp.hr_salary)})`}
                negative clickable
                onClick={() => openExpenseDrill('HR Salary (advanced by Funder)', cp.hr_salary_list)}
                icon={<Users className="w-3.5 h-3.5" />}
              />
              <StmtRow
                label="Less: HR Expenses — Non-Salary"
                value={`(${fmt(cp.hr_other)})`}
                negative clickable
                onClick={() => openExpenseDrill('HR Expenses — Non-Salary', cp.hr_other_list)}
                icon={<Users className="w-3.5 h-3.5" />}
              />
              <div className="flex items-center justify-between rounded-lg bg-emerald-600 text-white px-4 py-3 mt-3">
                <span className="font-semibold">Corporate Team Net P&amp;L</span>
                <span className="text-xl font-bold tabular-nums" data-testid="corp-net-pnl-value">{fmt(cp.net_pnl)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ===== Drill dialog ===== */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="fcpnl-drill-dialog">
          <DialogHeader>
            <DialogTitle>{drill?.title}</DialogTitle>
            <DialogDescription>Drill-down detail for FY {globalFY}</DialogDescription>
          </DialogHeader>
          {drill?.kind === 'estimates' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estimate #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type Source</TableHead>
                  <TableHead className="text-right">Revenue (excl GST)</TableHead>
                  <TableHead className="text-right">Funder Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drill.rows.map((r) => (
                  <TableRow key={r.estimate_id} className={r.type_discrepancy ? 'bg-amber-50' : ''}>
                    <TableCell className="font-mono text-xs">{r.estimate_number}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{r.customer_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{r.status}</Badge></TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-500">{r.type_source}</span>
                      {r.type_discrepancy && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" />}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(r.revenue_excl_gst)}</TableCell>
                    <TableCell className="text-right tabular-nums text-indigo-700">{fmt(r.funder_share)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {drill?.kind === 'expenses' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Company / Vendor</TableHead>
                  <TableHead>CRM ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drill.rows.map((r, i) => (
                  <TableRow key={r.id || i}>
                    <TableCell className="max-w-[160px] truncate">{r.category || r.expense_type}</TableCell>
                    <TableCell className="max-w-[160px] truncate">{r.company_name || r.vendor_name || r.training_name || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{r.corporate_project_id || '—'}</TableCell>
                    <TableCell className="text-xs">{r.reporting_date || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{r.payment_status || '—'}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Record repayment dialog */}
      <Dialog open={!!repayForm} onOpenChange={(o) => !o && setRepayForm(null)}>
        <DialogContent className="max-w-md" data-testid="fcpnl-repay-dialog">
          <DialogHeader>
            <DialogTitle>Record repayment to Funder</DialogTitle>
            <DialogDescription>Reduces the outstanding balance owed to the Funder for FY {globalFY}</DialogDescription>
          </DialogHeader>
          {repayForm && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" min="0" value={repayForm.amount} onChange={(e) => setRepayForm({ ...repayForm, amount: e.target.value })} data-testid="repay-amount" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={repayForm.repayment_date} onChange={(e) => setRepayForm({ ...repayForm, repayment_date: e.target.value })} data-testid="repay-date" />
              </div>
              <div>
                <Label className="text-xs">Against</Label>
                <Select value={repayForm.category} onValueChange={(v) => setRepayForm({ ...repayForm, category: v })}>
                  <SelectTrigger data-testid="repay-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="funder_share">Funder Share</SelectItem>
                    <SelectItem value="salary_advance">Salary Advance (loan)</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input value={repayForm.note} onChange={(e) => setRepayForm({ ...repayForm, note: e.target.value })} data-testid="repay-note" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRepayForm(null)}>Cancel</Button>
                <Button onClick={saveRepayment} disabled={saving} data-testid="repay-save">{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage repayments list */}
      <Dialog open={showRepayments} onOpenChange={setShowRepayments}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="fcpnl-repay-list-dialog">
          <DialogHeader>
            <DialogTitle>Funder repayments — FY {globalFY}</DialogTitle>
            <DialogDescription>Payments made to the Funder against share &amp; salary advance</DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Against</TableHead><TableHead>Note</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {repayments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No repayments recorded</TableCell></TableRow>}
              {repayments.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.repayment_date}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{(r.category || 'general').replace('_', ' ')}</Badge></TableCell>
                  <TableCell className="max-w-[180px] truncate text-xs">{r.note || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteRepayment(r.id)} data-testid={`repay-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StmtRow = ({ label, value, strong, negative, divider, clickable, onClick, icon }) => (
  <div
    className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
      divider ? 'bg-slate-100' : ''
    } ${clickable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
    onClick={clickable ? onClick : undefined}
    data-testid={clickable ? `stmt-row-${label}` : undefined}
  >
    <span className={`flex items-center gap-1.5 ${strong ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
      {icon}{label}
      {clickable && <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
    </span>
    <span className={`tabular-nums ${negative ? 'text-red-600' : strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>{value}</span>
  </div>
);

export default FunderCorporatePnL;
